import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  CircularProgress,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  Close,
  Sync,
  CheckCircle,
  Error as ErrorIcon,
  Payment,
  Link as LinkIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import gatewayService, { Gateway } from '../../services/gatewayService';
import env from '../../utils/env';

interface ResultStats {
  total: number;
  sucessos: number;
  erros: number;
  detalhesErros?: string[];
}

interface AtualizaFaturasEfiModalProps {
  open: boolean;
  onClose: () => void;
  selectedGateway: Gateway | null;
  initialResultStats?: ResultStats | null;
  onSuccess?: () => void;
}

export const AtualizaFaturasEfiModal: React.FC<AtualizaFaturasEfiModalProps> = ({
  open,
  onClose,
  selectedGateway,
  initialResultStats,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [resultStats, setResultStats] = useState<ResultStats | null>(initialResultStats || null);

  useEffect(() => {
    setResultStats(initialResultStats || null);
  }, [initialResultStats, selectedGateway, open]);

  const isEfi = selectedGateway?.tipo === 'efi';

  const handleExecutar = async () => {
    if (!selectedGateway) {
      toast.error('Nenhum gateway selecionado.');
      return;
    }

    if (!isEfi) {
      toast.error('Apenas gateways do tipo Efí (Gerencianet) suportam essa atualização.');
      return;
    }

    try {
      setLoading(true);
      setResultStats(null);
      const res = await gatewayService.atualizarNotificationUrl(selectedGateway.id);
      setResultStats(res);
      toast.success(`Atualização concluída: ${res.sucessos} faturas atualizadas!`);
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error('Erro ao atualizar URL de notificação:', error);
      toast.error(error?.response?.data?.message || 'Erro ao atualizar URL de notificação nas faturas.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Sync color="primary" />
            <Typography variant="h6">Atualizar URL de Notificação Efíbank</Typography>
          </Box>
          <IconButton onClick={onClose} disabled={loading} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {selectedGateway && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: 'rgba(25, 118, 210, 0.04)' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight="bold" display="flex" alignItems="center" gap={1}>
                <Payment fontSize="small" color="primary" />
                {selectedGateway.nome}
              </Typography>
              <Chip
                label={selectedGateway.tipo.toUpperCase()}
                color={isEfi ? 'primary' : 'warning'}
                size="small"
                variant="outlined"
              />
            </Box>
          </Paper>
        )}

        {!isEfi && selectedGateway && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            O gateway selecionado (<strong>{selectedGateway.nome}</strong> - {selectedGateway.tipo}) não é um gateway Efí. Esta funcionalidade é exclusiva para o gateway Efíbank (Gerencianet).
          </Alert>
        )}

        {isEfi && (
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              A URL de notificação utilizada é configurada na variável <strong>BACKEND_URL</strong> do sistema, enviada via <code>PUT /v1/charge/:id/metadata</code> no Efíbank:
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, bg: '#f5f5f5', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <LinkIcon fontSize="small" color="primary" />
              <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                {`${env.API_URL}/api/webhooks/efi`}
              </Typography>
            </Paper>
          </Box>
        )}

        {resultStats && (
          <Paper variant="outlined" sx={{ p: 2, mt: 2, backgroundColor: resultStats.erros > 0 ? '#fff8f6' : '#f6fff8' }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom display="flex" alignItems="center" gap={1}>
              {resultStats.erros > 0 ? <ErrorIcon color="error" /> : <CheckCircle color="success" />}
              Resultado da Operação
            </Typography>
            <Typography variant="body2">Total de faturas processadas: <strong>{resultStats.total}</strong></Typography>
            <Typography variant="body2" color="success.main">Sucessos: <strong>{resultStats.sucessos}</strong></Typography>

            {resultStats.erros > 0 && (
              <>
                <Typography variant="body2" color="error.main" mt={0.5}>Erros: <strong>{resultStats.erros}</strong></Typography>
                {resultStats.detalhesErros && resultStats.detalhesErros.length > 0 && (
                  <List dense sx={{ maxHeight: 150, overflow: 'auto', mt: 1, borderRadius: 1 }}>
                    {resultStats.detalhesErros.map((err, idx) => (
                      <ListItem key={idx} sx={{ py: 0 }}>
                        <ListItemText
                          primary={<Typography variant="caption" color="error">{err}</Typography>}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            )}
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          Fechar
        </Button>
        {isEfi && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleExecutar}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Sync />}
          >
            {loading ? 'Executando...' : (resultStats ? 'Executar Novamente' : 'Confirmar e Executar')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AtualizaFaturasEfiModal;
